import {
  type ContractDetail,
  type ContractorExcessView,
  can,
  FUNDING_SOURCES,
  type FundingSource,
  PAYMENT_KINDS,
  PAYMENT_PRIORITIES,
  PAYMENT_REQUEST_STATUSES,
  type PaymentKind,
  type PaymentPriority,
  type PaymentRequestStatus,
  type PaymentRequestView,
  PROCUREMENT_STAGES,
  type ProcurementOverview,
  type PurchaseRequestDetail,
  type PurchaseRequestSummary,
  REGISTRY_STATUSES,
  type RegistryView,
  seesMoney,
} from '@epc/contracts';
import { type QueryClient, queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import clsx from 'clsx';
import { Download, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date, dateTime, plural, tenge, tengeShort, todayLocal } from '../../lib/format';
import { counterpartiesQuery, projectQuery, projectsQuery } from '../../lib/projects';
import { useCan, useMe } from '../../lib/session';
import { Button, IconButton } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, Spinner, type Tone, toast } from '../../ui/feedback';
import { Field, Input, Select } from '../../ui/form';
import { Page, Panel, TitleBlock } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import { peopleQuery } from '../warehouse/queries';
import { InvoiceMatchPanel } from './paymentMatch';
import {
  DuplicatesPanel,
  openPaymentRows,
  PaymentDocsList,
  PlanSection,
  scheduleOption,
  usePaymentRequest,
} from './paymentParts';

type PrQuery = {
  projectId?: string;
  requestId?: string;
  contractId?: string;
  registryId?: string;
  status?: PaymentRequestStatus;
  overdue?: boolean;
};

export const paymentRequestsQuery = (q: PrQuery) =>
  queryOptions({
    queryKey: ['procurement', 'payment-requests', q],
    queryFn: () => api<PaymentRequestView[]>('/procurement/payment-requests', { query: q }),
  });
export const registriesQuery = (q: { status?: 'draft' | 'sent'; legalEntityId?: string } = {}) =>
  queryOptions({
    queryKey: ['procurement', 'registries', q],
    queryFn: () => api<RegistryView[]>('/procurement/registries', { query: q }),
  });
export const registryQuery = (id: string) =>
  queryOptions({
    queryKey: ['procurement', 'registry', id],
    queryFn: () => api<RegistryView>(`/procurement/registries/${id}`),
  });
export type OverviewFilters = {
  stage?: string;
  userId?: string;
  supplierId?: string;
  from?: string;
  to?: string;
  objectId?: string;
  department?: string;
};
export const overviewQuery = (projectId?: string, f: OverviewFilters = {}) =>
  queryOptions({
    queryKey: ['procurement', 'overview', projectId ?? 'all', f],
    queryFn: () =>
      api<ProcurementOverview>('/procurement/overview', {
        query: {
          projectId,
          stage: f.stage || undefined,
          userId: f.userId || undefined,
          supplierId: f.supplierId || undefined,
          from: f.from || undefined,
          to: f.to || undefined,
          objectId: f.objectId || undefined,
          department: f.department || undefined,
        },
      }),
  });

function refresh(qc: QueryClient) {
  for (const k of [
    'procurement',
    'payments',
    'finplan',
    'cashflow',
    'project',
    'portfolio',
    'contracts',
    'attention',
  ])
    qc.invalidateQueries({ queryKey: [k] });
}

const STATUS_TONE: Record<PaymentRequestStatus, Tone> = {
  draft: 'neutral',
  hold: 'danger',
  approval: 'warning',
  sent: 'info',
  paid: 'success',
  cancelled: 'neutral',
};

const num = (v: string) => Number(v.replace(/\s/g, '').replace(',', '.'));
const todayIso = () => todayLocal();
/** Выбор «вне графика платежей договора» */
const NONE = '__none';

/* ───────────────────────── Заявка на оплату: действия ───────────────────────── */

function PaymentRequestDialog({ pr: initial, onClose }: { pr: PaymentRequestView; onClose: () => void }) {
  const qc = useQueryClient();
  const me = useMe();
  // Карточка обновляется после загрузки файла, решения по повторной оплате, переноса даты
  const pr = usePaymentRequest(initial);
  // Права — по проекту заявки, как проверяет API
  const project = useQuery(projectQuery(pr.projectId));
  const p = project.data;
  const target = p
    ? {
        legalEntityId: p.legalEntityId,
        directionId: p.directionId,
        projectId: p.id,
        projectPath: [p.parentId],
      }
    : { projectId: pr.projectId };
  const canRequest = can(me.access, 'procurement.payment.request', target);
  const canPay = can(me.access, 'procurement.payment.confirm', target);
  // Остановленную заявку разбирает финансовый менеджер юрлица или компании этого проекта (ФН-21, ФН-02),
  // и не тот, кто её готовил или передал
  const owner = Boolean(me.tenant?.isOwner);
  const canDecide =
    (owner ||
      (can(me.access, 'finance.manager', { legalEntityId: p?.legalEntityId }) &&
        pr.createdById !== me.user.id &&
        pr.sentById !== me.user.id)) &&
    seesMoney(me.access, 'acts_invoices');
  // Дату, очерёдность и источник денег меняют ещё финансы проекта
  const canPlan = canRequest || canPay || canDecide || can(me.access, 'finance.edit', target);
  const [reason, setReason] = useState('');
  const [paid, setPaid] = useState(pr.amount !== null ? String(pr.amount) : '');
  const [paidOn, setPaidOn] = useState(todayIso());
  const [oneC, setOneC] = useState({
    oneCNumber: pr.oneCNumber ?? '',
    oneCDate: pr.oneCDate ?? '',
    oneCStatus: pr.oneCStatus ?? '',
  });
  const act = useMutation({
    mutationFn: (body: {
      action: 'send' | 'pay' | 'cancel';
      reason?: string;
      paidAmount?: number;
      paidOn?: string;
    }) => api<PaymentRequestView>(`/procurement/payment-requests/${pr.id}/action`, { body }),
    onSuccess: (v) => {
      refresh(qc);
      toast(`${v.number}: ${PAYMENT_REQUEST_STATUSES[v.status].toLowerCase()}`);
      onClose();
    },
  });
  const save1c = useMutation({
    mutationFn: () =>
      api<PaymentRequestView>(`/procurement/payment-requests/${pr.id}`, {
        method: 'PATCH',
        body: {
          oneCNumber: oneC.oneCNumber.trim() || null,
          oneCDate: oneC.oneCDate || null,
          oneCStatus: oneC.oneCStatus.trim() || null,
        },
      }),
    onSuccess: () => {
      refresh(qc);
      toast('Данные 1С сохранены');
    },
  });
  const open = ['draft', 'hold', 'approval', 'sent'].includes(pr.status);
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      width="lg"
      title={`Заявка на оплату ${pr.number}`}
      description={`${PAYMENT_KINDS[pr.kind]} · ${PAYMENT_REQUEST_STATUSES[pr.status]}${pr.contractNumber ? ` · договор ${pr.contractNumber}` : ''}`}
    >
      <div className="flex flex-col gap-5">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-[14px] sm:grid-cols-4">
          <div>
            <dt className="text-[12px] text-ink-2">Сумма</dt>
            <dd className="num">{tenge(pr.amount)}</dd>
          </div>
          <div>
            <dt className="text-[12px] text-ink-2">Плановая дата</dt>
            <dd className={clsx('num', pr.overdue && 'text-danger')}>{date(pr.plannedDate)}</dd>
          </div>
          <div>
            <dt className="text-[12px] text-ink-2">Поставщик</dt>
            <dd>{pr.counterpartyName ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-[12px] text-ink-2">Статья</dt>
            <dd>{pr.costArticle ?? '—'}</dd>
          </div>
        </dl>
        {pr.overReason ? (
          <p className="rounded-md bg-warning-soft px-4 py-3 text-[14px]">
            <b>Сверх суммы договора:</b> {pr.overReason}
            {pr.overApprovedByName
              ? ` — согласовал ${pr.overApprovedByName}, ${dateTime(pr.overApprovedAt)}`
              : ''}
          </p>
        ) : null}
        {pr.status === 'approval' ? (
          <p className="rounded-md bg-warning-soft px-4 py-3 text-[14px]">
            Оплату сверх суммы договора больше не согласуют: оформите допсоглашение к договору и передайте
            заявку снова, когда его подпишут, или отмените заявку (ЗК-15).
          </p>
        ) : null}
        {pr.status === 'paid' ? (
          <p className="rounded-md bg-success-soft px-4 py-3 text-[14px]">
            Оплачено {tenge(pr.paidAmount)} {date(pr.paidOn)}; факт оплаты попал в проект и финансовый план.
          </p>
        ) : null}
        {pr.excessWithheld ? (
          <p className="rounded-md bg-info-soft px-4 py-3 text-[14px]">
            Удержан перерасход давальческих материалов подрядчика {tenge(pr.excessWithheld)}: сумма к оплате
            уже уменьшена на него. Если заявку отменить, удержание снимется и перерасход будет предложен в
            следующей заявке (СК-24).
          </p>
        ) : null}

        <DuplicatesPanel pr={pr} canDecide={canDecide} canRequest={canRequest} />
        <PlanSection pr={pr} canEditDraft={canRequest} canEditPlan={canPlan} />
        <InvoiceMatchPanel pr={pr} canEdit={canRequest} />
        <PaymentDocsList
          pr={pr}
          editable={(pr.status === 'draft' || pr.status === 'approval') && canRequest}
        />
        {(pr.status === 'draft' || pr.status === 'approval') && canRequest ? (
          <section className="flex flex-col gap-2">
            <p className="text-[13px] text-ink-2">
              Оплаты не превышают сумму договора: доплата — после подписанного допсоглашения (ЗК-15).
            </p>
            {pr.registryId && !pr.registrySent ? (
              <p className="text-[13px] text-ink-2">
                Передать на оплату можно после того, как реестр {pr.registryNumber} передан в финансы.
              </p>
            ) : null}
            {!pr.plannedDate || (!pr.invoiceNumber && !pr.purpose) ? (
              <p className="text-[13px] text-ink-2">
                Перед передачей укажите плановую дату и номер счёта или назначение платежа: по ним платёжный
                календарь и проверка повторной оплаты (ФН-02, ФН-21).
              </p>
            ) : null}
            <div>
              <Button
                variant="primary"
                loading={act.isPending}
                disabled={
                  Boolean(pr.registryId && !pr.registrySent) ||
                  !pr.plannedDate ||
                  (!pr.invoiceNumber && !pr.purpose)
                }
                onClick={() => act.mutate({ action: 'send' })}
              >
                Передать на оплату
              </Button>
            </div>
          </section>
        ) : null}
        {pr.status === 'sent' && canPay && seesMoney(me.access, 'acts_invoices') ? (
          <section className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
            <Field label="Фактически оплачено, ₸">
              <Input inputMode="decimal" value={paid} onChange={(e) => setPaid(e.target.value)} />
            </Field>
            <Field label="Дата оплаты">
              <Input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
            </Field>
            <Button
              variant="primary"
              loading={act.isPending}
              disabled={!num(paid) || !paidOn}
              onClick={() => act.mutate({ action: 'pay', paidAmount: num(paid), paidOn })}
            >
              Оплачено
            </Button>
          </section>
        ) : null}

        {pr.status !== 'cancelled' && (canRequest || canPay) ? (
          <section className="flex flex-col gap-3 border-t border-line pt-4">
            <h3 className="text-[14px] font-semibold">1С, этап 1: ручные поля</h3>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Номер в 1С">
                <Input
                  value={oneC.oneCNumber}
                  onChange={(e) => setOneC({ ...oneC, oneCNumber: e.target.value })}
                />
              </Field>
              <Field label="Дата в 1С">
                <Input
                  type="date"
                  value={oneC.oneCDate}
                  onChange={(e) => setOneC({ ...oneC, oneCDate: e.target.value })}
                />
              </Field>
              <Field label="Статус в 1С">
                <Input
                  value={oneC.oneCStatus}
                  onChange={(e) => setOneC({ ...oneC, oneCStatus: e.target.value })}
                />
              </Field>
            </div>
            <div>
              <Button loading={save1c.isPending} onClick={() => save1c.mutate()}>
                Сохранить данные 1С
              </Button>
            </div>
            <ErrorBox error={save1c.error} />
          </section>
        ) : null}

        {open && canRequest ? (
          <section className="flex flex-col gap-2 border-t border-line pt-4">
            <Field label="Причина отмены">
              <Input value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
            <div>
              <Button
                variant="danger"
                disabled={!reason.trim()}
                loading={act.isPending}
                onClick={() => act.mutate({ action: 'cancel', reason: reason.trim() })}
              >
                Отменить заявку на оплату
              </Button>
            </div>
          </section>
        ) : null}
        {pr.cancelReason ? <p className="text-[14px] text-ink-2">Отменена: {pr.cancelReason}</p> : null}
        <ErrorBox error={act.error} />
      </div>
    </Dialog>
  );
}

/**
 * Давальческие у подрядчика (СК-24): перерасход сверх нормы по отчётам подрядчика, утверждённым руководителем
 * проекта, удерживается при оплате его актов — один раз: сервер отмечает отчёты этой заявкой и сверяет сумму.
 * По нерешённым отчётам — только предупреждение. Без права готовить заявки или без перерасхода подсказки нет.
 */
function ContractorExcessHint({
  contractId,
  withheld,
  onToggle,
}: {
  contractId: string;
  withheld: number | null;
  onToggle: (amount: number | null) => void;
}) {
  const q = useQuery({
    queryKey: ['procurement', 'payment-excess', contractId],
    queryFn: () =>
      api<ContractorExcessView>('/procurement/payment-requests/excess', { query: { contractId } }),
    retry: false,
  });
  const v = q.data;
  if (!v?.applicable || !v.lines.length) return null;
  const line = (l: ContractorExcessView['lines'][number]) =>
    `${l.itemName} — ${fmtQty.format(l.excess)} ${l.unit ?? ''}`.trim();
  const approved = v.lines.filter((l) => l.state === 'approved');
  const pending = v.lines.filter((l) => l.state === 'pending');
  const amount = v.approvedAmount ?? 0;
  return (
    <div className="rounded-md border border-warning/40 bg-warning-soft px-4 py-3 text-[14px] text-ink sm:col-span-2">
      {approved.length ? (
        <>
          <p className="font-medium">
            Перерасход давальческих материалов сверх нормы, утверждённый руководителем проекта
            {v.approvedAmount ? `: ${tenge(v.approvedAmount)}` : ''} — удерживается из оплаты акта (СК-24)
          </p>
          <p className="mt-1 text-[13px] text-ink-2">{approved.slice(0, 5).map(line).join('; ')}</p>
          {v.unpriced ? (
            <p className="mt-1 text-[13px] text-ink-2">
              У части материалов нет цены на складе подрядчика: такие отчёты не удерживаются, пока склад не
              даст цену.
            </p>
          ) : null}
          {amount > 0 ? (
            <Button
              variant="secondary"
              size="md"
              className="mt-3"
              onClick={() => onToggle(withheld === null ? amount : null)}
            >
              {withheld === null ? 'Удержать из суммы' : 'Не удерживать'}
            </Button>
          ) : null}
        </>
      ) : null}
      {pending.length ? (
        <p className={clsx('text-[13px] text-ink-2', approved.length && 'mt-2')}>
          Ждут решения руководителя проекта
          {v.pendingAmount ? ` (${tenge(v.pendingAmount)})` : ''}: {pending.slice(0, 5).map(line).join('; ')}.
          Удержать можно после утверждения отчёта.
        </p>
      ) : null}
    </div>
  );
}

const fmtQty = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 3 });

export function NewPaymentRequestDialog({
  requestId,
  contractId,
  suggested,
  onClose,
}: {
  requestId?: string;
  contractId?: string;
  suggested?: number | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  // ФН-02: заявка закрывает плановый платёж из графика договора — сумма и дата подставляются из него
  const contract = useQuery({
    queryKey: ['contracts', 'detail', contractId],
    queryFn: () => api<ContractDetail>(`/contracts/${contractId}`),
    enabled: Boolean(contractId),
  });
  const rows = openPaymentRows(contract.data);
  const [f, setF] = useState({
    kind: 'advance' as PaymentKind,
    amount: suggested ? String(Math.round(suggested * 100) / 100) : '',
    plannedDate: todayIso(),
    note: '',
    /** '' — не выбран, NONE — вне графика платежей договора */
    scheduleRowId: '',
    priority: 'normal' as PaymentPriority,
    fundingSource: '' as FundingSource | '',
    invoiceNumber: '',
    invoiceDate: '',
    purpose: '',
  });
  // Открытый плановый платёж один — он и подставляется: сумма, дата и тип платежа из графика (ФН-02)
  const [auto, setAuto] = useState(false);
  if (!auto && contract.data) {
    setAuto(true);
    if (rows.length === 1) {
      const r = rows[0]!;
      const left = r.amount != null ? Math.max(0, r.amount - (r.requested ?? 0)) : null;
      setF({
        ...f,
        scheduleRowId: r.id,
        kind: r.basis === 'advance' ? 'advance' : r.basis === 'retention' ? 'final' : f.kind,
        amount: left ? String(Math.round(left * 100) / 100) : f.amount,
        plannedDate: r.plannedDate ?? f.plannedDate,
      });
    }
  }
  const pickRow = (id: string) => {
    const r = rows.find((x) => x.id === id);
    if (!r) return setF({ ...f, scheduleRowId: id === NONE ? NONE : '' });
    const left = r.amount != null ? Math.max(0, r.amount - (r.requested ?? 0)) : null;
    setF({
      ...f,
      scheduleRowId: id,
      kind: r.basis === 'advance' ? 'advance' : r.basis === 'retention' ? 'final' : f.kind,
      amount: left ? String(Math.round(left * 100) / 100) : f.amount,
      plannedDate: r.plannedDate ?? f.plannedDate,
    });
  };
  /**
   * Удержание перерасхода давальческих (СК-24): сумма, которую человек видел в подсказке. В поле — сумма по
   * акту (как из планового платежа), к оплате уходит она за вычетом удержания; из аванса не удерживается
   */
  const [withheld, setWithheld] = useState<number | null>(null);
  const withholding = withheld !== null && f.kind !== 'advance';
  const payable = Math.round((num(f.amount) - (withholding ? withheld : 0)) * 100) / 100;
  const m = useMutation({
    mutationFn: () =>
      api<PaymentRequestView>('/procurement/payment-requests', {
        body: {
          ...(withholding ? { withholdExcess: withheld } : {}),
          requestId: requestId ?? null,
          contractId: contractId ?? null,
          kind: f.kind,
          amount: payable,
          plannedDate: f.plannedDate || null,
          note: f.note.trim() || null,
          // Вне графика — явно без планового платежа; без выбора (графика нет) — решает сервер
          ...(f.scheduleRowId === NONE
            ? { scheduleRowId: null }
            : f.scheduleRowId
              ? { scheduleRowId: f.scheduleRowId }
              : {}),
          priority: f.priority,
          fundingSource: f.fundingSource || null,
          invoiceNumber: f.invoiceNumber.trim() || null,
          invoiceDate: f.invoiceDate || null,
          purpose: f.purpose.trim() || null,
        },
      }),
    onSuccess: (v) => {
      refresh(qc);
      toast(`Создана ${v.number}`);
      onClose();
    },
    // Перерасход могла удержать другая заявка: подсказка перечитывается
    onError: () => qc.invalidateQueries({ queryKey: ['procurement', 'payment-excess'] }),
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Новая заявка на оплату"
      description="Тип платежа и сумма; по одной заявке или договору может быть несколько оплат (ЗК-15)."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={m.isPending}
            disabled={payable <= 0 || (rows.length > 0 && !f.scheduleRowId)}
            onClick={() => m.mutate()}
          >
            Создать заявку на оплату
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {contractId && f.kind !== 'advance' ? (
          <ContractorExcessHint contractId={contractId} withheld={withheld} onToggle={setWithheld} />
        ) : null}
        {rows.length ? (
          <Field
            label="Плановый платёж договора"
            className="sm:col-span-2"
            hint="Из графика платежей: заявка закрывает его в платёжном календаре (ФН-02)"
          >
            <Select value={f.scheduleRowId} onChange={(e) => pickRow(e.target.value)}>
              <option value="">выберите плановый платёж</option>
              {rows.map((r) => (
                <option key={r.id} value={r.id}>
                  {scheduleOption(r)}
                </option>
              ))}
              <option value={NONE}>вне графика платежей договора</option>
            </Select>
          </Field>
        ) : null}
        <Field label="Тип платежа">
          <Select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as PaymentKind })}>
            {Object.entries(PAYMENT_KINDS).map(([k, t]) => (
              <option key={k} value={k}>
                {t}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label={withholding ? 'Сумма по акту, ₸' : 'Сумма, ₸'}
          hint={
            withholding
              ? `К оплате ${tenge(Math.max(0, payable))} — за вычетом удержания ${tenge(withheld)}; основание портал допишет в назначение платежа`
              : undefined
          }
        >
          <Input
            inputMode="decimal"
            value={f.amount}
            onChange={(e) => setF({ ...f, amount: e.target.value })}
          />
        </Field>
        <Field label="Плановая дата оплаты">
          <Input
            type="date"
            value={f.plannedDate}
            onChange={(e) => setF({ ...f, plannedDate: e.target.value })}
          />
        </Field>
        <Field label="Очерёдность">
          <Select
            value={f.priority}
            onChange={(e) => setF({ ...f, priority: e.target.value as PaymentPriority })}
          >
            {Object.entries(PAYMENT_PRIORITIES).map(([k, t]) => (
              <option key={k} value={k}>
                {t}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Номер счёта поставщика">
          <Input value={f.invoiceNumber} onChange={(e) => setF({ ...f, invoiceNumber: e.target.value })} />
        </Field>
        <Field label="Дата счёта">
          <Input
            type="date"
            value={f.invoiceDate}
            onChange={(e) => setF({ ...f, invoiceDate: e.target.value })}
          />
        </Field>
        <Field label="Источник денег">
          <Select
            value={f.fundingSource}
            onChange={(e) => setF({ ...f, fundingSource: e.target.value as FundingSource | '' })}
          >
            <option value="">не указан</option>
            {Object.entries(FUNDING_SOURCES).map(([k, t]) => (
              <option key={k} value={k}>
                {t}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Назначение платежа"
          className="sm:col-span-2"
          hint="Номер счёта или назначение нужны для передачи на оплату: по ним портал находит возможную повторную оплату (ФН-21)"
        >
          <Input value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} />
        </Field>
        <Field label="Примечание" className="sm:col-span-2">
          <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        </Field>
      </div>
      <ErrorBox error={m.error} className="mt-3" />
    </Dialog>
  );
}

export function PaymentRequestsTable({
  list,
  showSource = true,
  empty = 'Заявок на оплату нет.',
}: {
  list: PaymentRequestView[];
  showSource?: boolean;
  empty?: string;
}) {
  const [open, setOpen] = useState<PaymentRequestView | null>(null);
  if (!list.length) return <p className="p-5 text-[14px] text-ink-2">{empty}</p>;
  return (
    <div>
      <ScrollX>
        <table className="w-full min-w-[860px] text-[14px]">
          <thead>
            <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
              <th className="py-2.5 pr-3 pl-5 font-medium">Заявка на оплату</th>
              {showSource ? <th className="py-2.5 pr-3 font-medium">Основание</th> : null}
              <th className="py-2.5 pr-3 font-medium">Поставщик</th>
              <th className="py-2.5 pr-3 text-right font-medium">Сумма</th>
              <th className="py-2.5 pr-3 font-medium">План</th>
              <th className="py-2.5 pr-3 font-medium">Статус</th>
              <th className="py-2.5 pr-3 font-medium">1С</th>
              <th className="py-2.5 pr-5 text-right font-medium">Оплачено</th>
            </tr>
          </thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.id} className="border-t border-line">
                <td className="py-2.5 pr-3 pl-5">
                  <button
                    type="button"
                    onClick={() => setOpen(p)}
                    className="inline-flex min-h-6 items-center text-left font-medium text-ink hover:underline"
                  >
                    {p.number}
                  </button>
                  <div className="text-[12px] text-ink-2">{PAYMENT_KINDS[p.kind]}</div>
                </td>
                {showSource ? (
                  <td className="py-2.5 pr-3">
                    {p.requestId ? (
                      <Link to={`/procurement/${p.requestId}`} className="hover:underline">
                        {p.requestNumber}
                      </Link>
                    ) : null}
                    {p.contractNumber ? (
                      <div className="text-[12px] text-ink-2">
                        <Link to={`/contracts/${p.contractId}`} className="hover:underline">
                          договор {p.contractNumber}
                        </Link>
                      </div>
                    ) : null}
                    <div className="text-[12px] text-ink-2">{p.projectCode}</div>
                  </td>
                ) : null}
                <td className="py-2.5 pr-3">{p.counterpartyName ?? '—'}</td>
                <td className="num py-2.5 pr-3 text-right">{tenge(p.amount)}</td>
                <td className={clsx('num py-2.5 pr-3', p.overdue && 'font-medium text-danger')}>
                  {date(p.plannedDate)}
                  {p.overdue ? <div className="text-[12px]">просрочено</div> : null}
                </td>
                <td className="py-2.5 pr-3">
                  <Badge tone={STATUS_TONE[p.status]}>{PAYMENT_REQUEST_STATUSES[p.status]}</Badge>
                </td>
                <td className="py-2.5 pr-3 text-[13px]">
                  {p.oneCNumber ? (
                    <>
                      {p.oneCNumber}
                      <div className="text-ink-2">
                        {[date(p.oneCDate), p.oneCStatus].filter((x) => x && x !== '—').join(', ')}
                      </div>
                    </>
                  ) : (
                    <span className="text-ink-3">—</span>
                  )}
                </td>
                <td className="num py-2.5 pr-5 text-right">
                  {p.status === 'paid' ? (
                    <>
                      {tenge(p.paidAmount)}
                      <div className="text-[12px] text-ink-2">{date(p.paidOn)}</div>
                    </>
                  ) : (
                    <span className="text-ink-3">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollX>
      {open ? <PaymentRequestDialog pr={open} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}

/* ───────────────────────── В карточке заявки: реестр и оплаты ───────────────────────── */

export function RequestPaymentsPanel({ d }: { d: PurchaseRequestDetail }) {
  const qc = useQueryClient();
  const canRegistry = useCan('procurement.registry');
  const canRequest = useCan('procurement.payment.request');
  const me = useMe();
  const money = seesMoney(me.access, 'acts_invoices');
  const project = useQuery(projectQuery(d.projectId));
  const le = project.data?.legalEntityId;
  const drafts = useQuery({
    ...registriesQuery({ status: 'draft', legalEntityId: le }),
    enabled: Boolean(le) && d.stage === 'registry',
  });
  const prs = useQuery(paymentRequestsQuery({ requestId: d.id }));
  const [target, setTarget] = useState('');
  const [creating, setCreating] = useState(false);
  const include = useMutation({
    mutationFn: async () => {
      let id = target;
      if (!id) {
        const r = await api<RegistryView>('/procurement/registries', { body: { legalEntityId: le } });
        id = r.id;
      }
      return api<RegistryView>(`/procurement/registries/${id}/requests`, { body: { requestIds: [d.id] } });
    },
    onSuccess: (r) => {
      refresh(qc);
      toast(`Заявка включена в реестр ${r.number}`);
    },
  });
  if (!['registry', 'payment', 'delivery', 'closed'].includes(d.stage) && !prs.data?.length) return null;
  const requested = (prs.data ?? [])
    .filter((p) => p.status !== 'cancelled')
    // Удержанный перерасход давальческих (СК-24) закрывает часть суммы вместе с оплатой
    .reduce((s, p) => s + (p.paidAmount ?? p.amount ?? 0) + (p.excessWithheld ?? 0), 0);
  const left = d.dueAmount !== null ? d.dueAmount - requested : null;
  return (
    <Panel
      title="Реестр и оплата"
      meta={
        d.dueAmount !== null
          ? `к оплате ${tengeShort(d.dueAmount)}, оплачено ${tengeShort(d.paidAmount)}, остаток ${tengeShort(d.dueAmount - (d.paidAmount ?? 0))}`
          : undefined
      }
      bodyClassName="p-0"
      actions={
        canRequest && money && ['payment', 'delivery'].includes(d.stage) ? (
          <Button icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
            Заявка на оплату
          </Button>
        ) : null
      }
    >
      {d.stage === 'registry' ? (
        <div className="flex flex-wrap items-end gap-3 border-b border-line p-5">
          {canRegistry ? (
            <>
              <Field label="Реестр" className="min-w-[260px]">
                <Select value={target} onChange={(e) => setTarget(e.target.value)}>
                  <option value="">Новый реестр</option>
                  {(drafts.data ?? []).map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.number}, {plural(r.count, 'заявка', 'заявки', 'заявок')}
                    </option>
                  ))}
                </Select>
              </Field>
              <Button
                variant="primary"
                loading={include.isPending}
                disabled={!le}
                onClick={() => include.mutate()}
              >
                Включить в реестр
              </Button>
              <ErrorBox error={include.error} />
            </>
          ) : (
            <p className="text-[14px] text-ink-2">
              Договор подписан — заявку включит в реестр ответственный за реестры.
            </p>
          )}
        </div>
      ) : d.registryId ? (
        <p className="border-b border-line px-5 py-3 text-[14px]">
          В реестре{' '}
          <Link to={`/procurement/registries/${d.registryId}`} className="font-medium hover:underline">
            {d.registryNumber}
          </Link>
        </p>
      ) : null}
      {prs.isPending ? (
        <div className="p-5">
          <Spinner />
        </div>
      ) : prs.isError ? (
        <ErrorBox error={prs.error} className="m-5" />
      ) : (
        <PaymentRequestsTable
          list={prs.data}
          showSource={false}
          empty={
            d.stage === 'registry'
              ? 'Заявки на оплату создаются после включения в реестр.'
              : 'Заявок на оплату ещё нет.'
          }
        />
      )}
      {creating ? (
        <NewPaymentRequestDialog
          requestId={d.id}
          contractId={d.signedContractId ?? undefined}
          suggested={left && left > 0 ? left : null}
          onClose={() => setCreating(false)}
        />
      ) : null}
    </Panel>
  );
}

/* ───────────────────────── Реестры ───────────────────────── */

export function RegistriesTab() {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const canRegistry = useCan('procurement.registry');
  const q = useQuery(registriesQuery());
  const [le, setLe] = useState(me.legalEntities[0]?.id ?? '');
  const create = useMutation({
    mutationFn: () => api<RegistryView>('/procurement/registries', { body: { legalEntityId: le } }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['procurement'] });
      toast(`Создан реестр ${r.number}`);
      navigate({ to: `/procurement/registries/${r.id}` });
    },
  });
  return (
    <Panel
      title="Реестры закупок"
      meta="несколько заявок → один реестр (ЗК-01)"
      bodyClassName="p-0"
      actions={
        canRegistry ? (
          <div className="flex items-center gap-2">
            {me.legalEntities.length > 1 ? (
              <Select value={le} onChange={(e) => setLe(e.target.value)} aria-label="Юрлицо">
                {me.legalEntities.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.shortName || x.name}
                  </option>
                ))}
              </Select>
            ) : null}
            <Button
              icon={<Plus className="size-4" />}
              loading={create.isPending}
              disabled={!le}
              onClick={() => create.mutate()}
            >
              Новый реестр
            </Button>
          </div>
        ) : null
      }
    >
      <ErrorBox error={create.error} className="m-4" />
      {q.isPending ? (
        <div className="p-5">
          <Spinner />
        </div>
      ) : q.isError ? (
        <ErrorBox error={q.error} className="m-5" />
      ) : q.data.length ? (
        <ScrollX>
          <table className="w-full min-w-[760px] text-[14px]">
            <thead>
              <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                <th className="py-2.5 pr-3 pl-5 font-medium">Реестр</th>
                <th className="py-2.5 pr-3 font-medium">Юрлицо</th>
                <th className="py-2.5 pr-3 font-medium">Статус</th>
                <th className="py-2.5 pr-3 text-right font-medium">Заявок</th>
                <th className="py-2.5 pr-3 text-right font-medium">Сумма</th>
                <th className="py-2.5 pr-5 text-right font-medium">Оплачено</th>
              </tr>
            </thead>
            <tbody>
              {q.data.map((r) => (
                <tr key={r.id} className="border-t border-line">
                  <td className="py-2.5 pr-3 pl-5">
                    <Link
                      to={`/procurement/registries/${r.id}`}
                      className="font-medium text-ink hover:underline"
                    >
                      {r.number}
                    </Link>
                    <div className="text-[12px] text-ink-2">{dateTime(r.createdAt)}</div>
                  </td>
                  <td className="py-2.5 pr-3">{r.legalEntityName}</td>
                  <td className="py-2.5 pr-3">
                    <Badge tone={r.status === 'sent' ? 'info' : 'neutral'}>
                      {REGISTRY_STATUSES[r.status]}
                    </Badge>
                  </td>
                  <td className="num py-2.5 pr-3 text-right">{r.count}</td>
                  <td className="num py-2.5 pr-3 text-right">{tenge(r.total)}</td>
                  <td className="num py-2.5 pr-5 text-right">{tenge(r.paid)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollX>
      ) : (
        <p className="p-5 text-[14px] text-ink-2">
          Реестров пока нет. В реестр попадают заявки на этапе «Готово к реестру» — после подписания договора
          или получения счёта.
        </p>
      )}
    </Panel>
  );
}

export function RegistryPage() {
  const { registryId } = useParams({ strict: false }) as { registryId: string };
  const qc = useQueryClient();
  const canRegistry = useCan('procurement.registry');
  const q = useQuery(registryQuery(registryId));
  useCrumbs(
    q.data
      ? [
          { label: 'Реестры', to: '/procurement?tab=registries' },
          { label: q.data.number, title: `Реестр ${q.data.number}` },
        ]
      : null,
    q.error,
  );
  const ready = useQuery({
    queryKey: ['procurement', 'requests', 'registry-ready'],
    queryFn: () => api<PurchaseRequestSummary[]>('/procurement/requests', { query: { stage: 'registry' } }),
  });
  const projects = useQuery(projectsQuery('all'));
  const prs = useQuery(paymentRequestsQuery({ registryId }));
  const [picked, setPicked] = useState<string[]>([]);
  const add = useMutation({
    mutationFn: () =>
      api<RegistryView>(`/procurement/registries/${registryId}/requests`, { body: { requestIds: picked } }),
    onSuccess: (r) => {
      refresh(qc);
      setPicked([]);
      toast(`В реестре ${plural(r.count, 'заявка', 'заявки', 'заявок')}`);
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      api<RegistryView>(`/procurement/registries/${registryId}/requests/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      refresh(qc);
      toast('Заявка исключена из реестра');
    },
  });
  const send = useMutation({
    mutationFn: () => api<RegistryView>(`/procurement/registries/${registryId}/send`, { method: 'POST' }),
    onSuccess: (r) => {
      refresh(qc);
      toast(`Реестр ${r.number} передан на оплату`);
    },
  });
  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const r = q.data;
  const leOf = (projectId: string) => projects.data?.find((p) => p.id === projectId)?.legalEntityId;
  const candidates = (ready.data ?? []).filter((x) => !x.registryId && leOf(x.projectId) === r.legalEntityId);
  const draft = r.status === 'draft';
  return (
    <Page>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-[24px] leading-8 font-semibold tracking-[-0.01em] text-ink">Реестр {r.number}</h1>
        <Badge tone={r.status === 'sent' ? 'info' : 'neutral'}>{REGISTRY_STATUSES[r.status]}</Badge>
      </div>
      <p className="mt-1 text-[14px] text-ink-2">
        {r.legalEntityName} · сформировал {r.createdByName ?? '—'} {dateTime(r.createdAt)}
        {r.sentAt ? ` · передал на оплату ${r.sentByName ?? '—'} ${dateTime(r.sentAt)}` : ''}
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        {draft && canRegistry ? (
          <Button
            variant="primary"
            loading={send.isPending}
            disabled={!r.count}
            onClick={() => send.mutate()}
          >
            Передать на оплату
          </Button>
        ) : null}
        <a
          href={`/api/v1/procurement/registries/${r.id}/export`}
          className="inline-flex h-10 items-center gap-2 rounded-md border border-line-strong bg-surface px-4 text-[14px] text-ink hover:bg-surface-2"
        >
          <Download className="size-4" /> Файл реестра
        </a>
      </div>
      <ErrorBox error={send.error ?? remove.error} className="mt-3" />

      <TitleBlock
        className="mt-6"
        items={[
          { label: 'Заявок в реестре', value: String(r.count) },
          { label: 'Сумма к оплате', value: tengeShort(r.total) },
          { label: 'Оплачено', value: tengeShort(r.paid), tone: 'success' },
          {
            label: 'Остаток',
            value: r.total !== null && r.paid !== null ? tengeShort(r.total - r.paid) : '—',
          },
        ]}
      />

      <div className="mt-6 flex flex-col gap-6">
        <Panel title="Заявки в реестре" bodyClassName="p-0">
          {r.requests.length ? (
            <ScrollX>
              <table className="w-full min-w-[860px] text-[14px]">
                <thead>
                  <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                    <th className="py-2.5 pr-3 pl-5 font-medium">Заявка</th>
                    <th className="py-2.5 pr-3 font-medium">Проект</th>
                    <th className="py-2.5 pr-3 font-medium">Поставщик, договор</th>
                    <th className="py-2.5 pr-3 font-medium">Статья</th>
                    <th className="py-2.5 pr-3 text-right font-medium">К оплате</th>
                    <th className="py-2.5 pr-3 text-right font-medium">Оплачено</th>
                    <th className="py-2.5 pr-5" />
                  </tr>
                </thead>
                <tbody>
                  {r.requests.map((x) => (
                    <tr key={x.id} className="border-t border-line">
                      <td className="py-2.5 pr-3 pl-5">
                        <Link to={`/procurement/${x.id}`} className="font-medium text-ink hover:underline">
                          {x.number} {x.title}
                        </Link>
                        <div className="text-[12px] text-ink-2">{PROCUREMENT_STAGES[x.stage]}</div>
                      </td>
                      <td className="py-2.5 pr-3 text-ink-2">{x.projectCode}</td>
                      <td className="py-2.5 pr-3">
                        {x.supplier ?? '—'}
                        {x.contractNumber ? (
                          <div className="text-[12px] text-ink-2">договор {x.contractNumber}</div>
                        ) : null}
                      </td>
                      <td className="py-2.5 pr-3 text-[13px]">{x.costArticle ?? '—'}</td>
                      <td className="num py-2.5 pr-3 text-right">{tenge(x.dueAmount)}</td>
                      <td className="num py-2.5 pr-3 text-right">{tenge(x.paidAmount)}</td>
                      <td className="py-2.5 pr-5 text-right">
                        {draft && canRegistry ? (
                          <IconButton
                            label={`Исключить ${x.number} из реестра`}
                            onClick={() => remove.mutate(x.id)}
                          >
                            <Trash2 className="size-4" />
                          </IconButton>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          ) : (
            <p className="p-5 text-[14px] text-ink-2">Реестр пуст.</p>
          )}
        </Panel>

        {draft && canRegistry ? (
          <Panel title="Готово к реестру" meta="заявки этого юрлица с подписанным договором или счётом">
            {candidates.length ? (
              <div className="flex flex-col gap-3">
                <ul className="flex flex-col gap-2">
                  {candidates.map((x) => (
                    <li key={x.id}>
                      <label className="flex items-start gap-3 text-[14px]">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={picked.includes(x.id)}
                          onChange={(e) =>
                            setPicked(e.target.checked ? [...picked, x.id] : picked.filter((y) => y !== x.id))
                          }
                        />
                        <span>
                          {x.number} {x.title}
                          <span className="block text-[12px] text-ink-2">
                            {x.projectCode} · {x.selectedSupplier ?? 'поставщик не выбран'} ·{' '}
                            {tenge(x.dueAmount)}
                          </span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
                <div>
                  <Button
                    variant="primary"
                    disabled={!picked.length}
                    loading={add.isPending}
                    onClick={() => add.mutate()}
                  >
                    Включить в реестр
                  </Button>
                </div>
                <ErrorBox error={add.error} />
              </div>
            ) : (
              <p className="text-[14px] text-ink-2">Заявок на этапе «Готово к реестру» нет.</p>
            )}
          </Panel>
        ) : null}

        <Panel title="Заявки на оплату по реестру" bodyClassName="p-0">
          {prs.isPending ? (
            <div className="p-5">
              <Spinner />
            </div>
          ) : prs.isError ? (
            <ErrorBox error={prs.error} className="m-5" />
          ) : (
            <PaymentRequestsTable
              list={prs.data}
              empty="Заявки на оплату создаются в карточке заявки на закуп."
            />
          )}
        </Panel>
      </div>
    </Page>
  );
}

/* ───────────────────────── Заявки на оплату: общий список ───────────────────────── */

export function PaymentRequestsTab() {
  const [filter, setFilter] = useState<'open' | PaymentRequestStatus | 'overdue' | 'all'>('open');
  const q = useQuery(
    paymentRequestsQuery(
      filter === 'overdue'
        ? { overdue: true }
        : filter === 'open' || filter === 'all'
          ? {}
          : { status: filter },
    ),
  );
  const list =
    filter === 'open'
      ? (q.data ?? []).filter((p) => ['draft', 'hold', 'approval', 'sent'].includes(p.status))
      : (q.data ?? []);
  const chips: Array<[typeof filter, string]> = [
    ['open', 'В работе'],
    ['hold', 'Остановлены'],
    ['approval', 'Сверх договора'],
    ['sent', 'Переданы на оплату'],
    ['overdue', 'Просроченные'],
    ['paid', 'Оплачены'],
    ['all', 'Все'],
  ];
  return (
    <Panel
      title="Заявки на оплату"
      meta="одна заявка или договор → несколько оплат (ЗК-15)"
      bodyClassName="p-0"
      actions={
        <div className="flex flex-wrap gap-1">
          {chips.map(([k, t]) => (
            <button
              key={k}
              type="button"
              onClick={() => setFilter(k)}
              className={clsx(
                'min-h-11 rounded-md px-3 text-[13px] font-medium md:min-h-8',
                filter === k ? 'bg-ink text-on-ink' : 'text-ink-2 hover:bg-tile hover:text-ink',
              )}
            >
              {t}
            </button>
          ))}
        </div>
      }
    >
      {q.isPending ? (
        <div className="p-5">
          <Spinner />
        </div>
      ) : q.isError ? (
        <ErrorBox error={q.error} className="m-5" />
      ) : (
        <PaymentRequestsTable list={list} />
      )}
    </Panel>
  );
}

/* ───────────────────────── Экран руководителя (ЗК-12) ───────────────────────── */

export function ProcurementOverviewStrip({ projectId }: { projectId?: string }) {
  const [f, setF] = useState<OverviewFilters>({});
  // Объект — у своего проекта: при смене проекта фильтр объекта снимается
  const [forProject, setForProject] = useState(projectId);
  if (forProject !== projectId) {
    setForProject(projectId);
    setF((prev) => ({ ...prev, objectId: undefined }));
  }
  const q = useQuery({ ...overviewQuery(projectId, f), placeholderData: (prev) => prev });
  const people = useQuery(peopleQuery);
  const suppliers = useQuery(counterpartiesQuery('supplier'));
  // Объекты — у выбранного проекта; подразделения — из заявок, которые человек видит (ЗК-12)
  const project = useQuery({ ...projectQuery(projectId ?? ''), enabled: Boolean(projectId) });
  const objects = project.data?.objects ?? [];
  const departments = q.data?.departments ?? [];
  const filters = (
    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
      <Select
        className="h-11"
        aria-label="Этап заявки"
        value={f.stage ?? ''}
        onChange={(e) => setF({ ...f, stage: e.target.value })}
      >
        <option value="">Все этапы</option>
        {(Object.keys(PROCUREMENT_STAGES) as Array<keyof typeof PROCUREMENT_STAGES>)
          .filter((k) => k !== 'cancelled')
          .map((k) => (
            <option key={k} value={k}>
              {PROCUREMENT_STAGES[k]}
            </option>
          ))}
      </Select>
      <Select
        className="h-11"
        aria-label="Ответственный"
        value={f.userId ?? ''}
        onChange={(e) => setF({ ...f, userId: e.target.value })}
      >
        <option value="">Все ответственные</option>
        {(people.data ?? []).map((p) => (
          <option key={p.id} value={p.id}>
            {p.fullName}
          </option>
        ))}
      </Select>
      <Select
        className="h-11"
        aria-label="Выбранный поставщик"
        value={f.supplierId ?? ''}
        onChange={(e) => setF({ ...f, supplierId: e.target.value })}
      >
        <option value="">Все поставщики</option>
        {(suppliers.data ?? []).map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </Select>
      <Input
        className="h-11"
        type="date"
        aria-label="Заявки с даты"
        value={f.from ?? ''}
        onChange={(e) => setF({ ...f, from: e.target.value })}
      />
      <Input
        className="h-11"
        type="date"
        aria-label="Заявки по дату"
        value={f.to ?? ''}
        onChange={(e) => setF({ ...f, to: e.target.value })}
      />
      {objects.length > 0 && (
        <Select
          className="h-11"
          aria-label="Объект"
          value={f.objectId ?? ''}
          onChange={(e) => setF({ ...f, objectId: e.target.value })}
        >
          <option value="">Все объекты</option>
          {objects.map((o) => (
            <option key={o.id} value={o.id}>
              {o.code ? `${o.code} · ${o.name}` : o.name}
            </option>
          ))}
        </Select>
      )}
      {(departments.length > 0 || f.department) && (
        <Select
          className="h-11"
          aria-label="Подразделение"
          value={f.department ?? ''}
          onChange={(e) => setF({ ...f, department: e.target.value })}
        >
          <option value="">Все подразделения</option>
          {departments.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </Select>
      )}
    </div>
  );
  if (q.isPending) return null;
  if (q.isError)
    return (
      <section aria-label="Сводка закупок" className="mb-6 flex flex-col gap-3">
        {filters}
        <ErrorBox error={q.error} />
      </section>
    );
  const { counts: c, overdue: o, money } = q.data;
  const steps: Array<[string, number, number | null]> = [
    ['Новые', c.fresh, null],
    ['Сбор КП', c.rfq, o.rfq],
    ['Анализ', c.analysis, null],
    ['Согласование', c.approval, o.approval],
    ['Подписание', c.signing, o.signing],
    ['Готово к реестру', c.readyForRegistry, o.registry],
    ['В реестрах', c.inRegistry, null],
    ['На оплате', c.onPayment, o.payment],
    ['Оплачены', c.paid, null],
  ];
  return (
    <section
      aria-label="Сводка закупок"
      aria-busy={q.isFetching}
      className={clsx('mb-6 flex flex-col gap-3', q.isFetching && 'opacity-60')}
    >
      {filters}
      <div className="overflow-x-auto rounded-lg border border-line bg-surface">
        <ol className="flex min-w-max">
          <li className="flex flex-col gap-0.5 border-r border-line px-4 py-3">
            <span className="text-[12px] text-ink-2">Открыто</span>
            <span className="num text-[22px] leading-7 font-semibold">{c.open}</span>
          </li>
          {steps.map(([t, n, od]) => (
            <li key={t} className="flex min-w-[104px] flex-col gap-0.5 px-4 py-3">
              <span className="text-[12px] whitespace-nowrap text-ink-2">{t}</span>
              <span className="num text-[22px] leading-7 font-semibold">{n}</span>
              {od ? <span className="text-[12px] font-medium text-danger">просрочено {od}</span> : null}
            </li>
          ))}
        </ol>
      </div>
      {money ? (
        <TitleBlock
          items={[
            { label: 'Активные заявки', value: tengeShort(money.activeRequests) },
            { label: 'Договоры по заявкам', value: tengeShort(money.contracts) },
            { label: 'Передано на оплату', value: tengeShort(money.sentToPayment) },
            { label: 'Оплачено', value: tengeShort(money.paid), tone: 'success' },
            { label: 'Остаток по договорам', value: tengeShort(money.remainder) },
          ]}
        />
      ) : null}
    </section>
  );
}
